import { useMemo, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import {
  QuizRenderer,
  QuizSolveDescription,
  QuizStyleProvider,
  SubmitButton,
} from '@quebon/shared-learning'
import type {
  QuizMediaObject,
  QuizSolveResult,
  QuizSolveResultDescription,
} from '@quebon/shared-learning'
import {
  hasDisplayableCorrectAnswer,
  QuestionResultCard,
} from './QuestionResultCard'
import { SubjectiveGradeSelect, type SubjectiveGrade } from './SubjectiveGradeSelect'
import { hasSubmittedAnswer, toAnswerArray } from './answerArray'

const SUPPORTED_QUIZ_TYPES = new Set([
  'multi_choice', 'boolean', 'fill_blank', 'short_answer', 'select_text', 'drag_text',
  'coloring', 'counting', 'image_select', 'ordering', 'pairing', 'explanation', 'inline',
])

function getSupportedQuizType(snapshot: unknown): string | null {
  if (!snapshot || typeof snapshot !== 'object') return null
  const t = (snapshot as { type?: unknown }).type
  return typeof t === 'string' && SUPPORTED_QUIZ_TYPES.has(t) ? t : null
}

interface Props {
  quizId: string
  order: number
  questionSnapshot: unknown
  studentAnswer?: unknown
  correctAnswer?: unknown
  explanation?: string
  isCorrect?: boolean
  partialCredit?: number | null
  corrects?: (boolean | null)[]
  /**
   * solve  — 학생 풀이 UI(학생의 답 프리필 + "정답 확인" 버튼). onSolve 로 답 제출.
   * result  — 채점 결과 뷰(정오답 + 해설 직접 노출). 읽기 전용.
   * preview — 미응답 문항의 교사용 정답·해설 미리보기. 학생 답과 저장 컨트롤 없음.
   */
  mode?: 'solve' | 'result' | 'preview'
  onSolve?: (answers: string[]) => void
  onSubjectiveGrade?: (grade: SubjectiveGrade & { answers: string[] }) => void
  allowSubjectivePartialCredit?: boolean
  onAnswerChange?: (answers: string[]) => void
  hideSubmitButton?: boolean
  hideSolveDescription?: boolean
  isSubmitting?: boolean
}

interface PreviewQuestionAssets {
  description?: QuizSolveResultDescription | null
  commentaryImages?: QuizMediaObject[] | null
  vars?: Record<string, unknown>
}

function previewQuestionAssets(snapshot: unknown): PreviewQuestionAssets {
  if (!snapshot || typeof snapshot !== 'object') return {}
  const { description, commentaryImages, vars } = snapshot as {
    description?: unknown
    commentaryImages?: unknown
    vars?: unknown
  }
  return {
    description: description && typeof description === 'object'
      ? description as QuizSolveResultDescription
      : undefined,
    commentaryImages: Array.isArray(commentaryImages)
      ? commentaryImages as QuizMediaObject[]
      : undefined,
    vars: vars && typeof vars === 'object' ? vars as Record<string, unknown> : undefined,
  }
}

function resolveQuestionDescription(
  snapshot: unknown,
  explanation?: string,
): QuizSolveResultDescription | undefined {
  const description = previewQuestionAssets(snapshot).description
  if (!description && explanation == null) return undefined
  return {
    ...description,
    text: explanation ?? description?.text ?? undefined,
  }
}

function hasDisplayableDescription(description: QuizSolveResultDescription | undefined): boolean {
  return Boolean(
    description?.text?.trim()
    || description?.image
    || description?.imageMobile
    || description?.desmos,
  )
}

function PreviewHeader({ children }: { children: ReactNode }) {
  const { t } = useTranslation('coaching')

  return (
    <section
      data-testid="coaching-solution-preview"
      aria-label={t('solutionPreview.teacherLabel')}
      className="space-y-3 rounded-lg border border-sky-200 bg-sky-50/40 p-3"
    >
      <div className="flex items-center gap-2">
        <span className="rounded-full bg-sky-700 px-2 py-0.5 text-xs font-semibold text-white">
          {t('solutionPreview.teacherBadge')}
        </span>
        <span className="text-xs text-sky-800">{t('solutionPreview.readOnlyHint')}</span>
      </div>
      {children}
    </section>
  )
}

function PreviewDetails({
  quizId,
  questionSnapshot,
  correctAnswer,
  explanation,
  hideSolveDescription,
}: Pick<Props, 'quizId' | 'questionSnapshot' | 'correctAnswer' | 'explanation' | 'hideSolveDescription'>) {
  const { t } = useTranslation('coaching')
  // 정답은 위 QuizRenderer에서 시각화하므로 부가 해설만 생략한다.
  if (hideSolveDescription) return null

  const hasCorrectAnswer = hasDisplayableCorrectAnswer(correctAnswer)
  const assets = previewQuestionAssets(questionSnapshot)
  const description = resolveQuestionDescription(questionSnapshot, explanation)
  const hasExplanation = hasDisplayableDescription(description)

  if (!hasCorrectAnswer && !hasExplanation) {
    return (
      <p className="rounded-md border border-gray-200 bg-white p-3 text-sm text-gray-600">
        {t('solutionPreview.empty')}
      </p>
    )
  }

  if (!hasExplanation) return null

  return (
    <div className="space-y-3" data-testid="solution-preview-details">
      <QuizSolveDescription
        description={description}
        commentaryImages={assets.commentaryImages}
        vars={assets.vars}
        id={`${quizId}-teacher-preview-description`}
        actionVisible={false}
      />
    </div>
  )
}

export function AdultQuestionView({
  quizId,
  order,
  questionSnapshot,
  studentAnswer,
  correctAnswer,
  explanation,
  isCorrect,
  partialCredit,
  corrects,
  mode = 'result',
  onSolve,
  onSubjectiveGrade,
  allowSubjectivePartialCredit = false,
  onAnswerChange,
  hideSubmitButton = false,
  hideSolveDescription = false,
  isSubmitting = false,
}: Props) {
  const { t } = useTranslation('coaching')
  const supportedType = getSupportedQuizType(questionSnapshot)
  const studentAnswers = useMemo(() => toAnswerArray(studentAnswer), [studentAnswer])
  const [draftAnswers, setDraftAnswers] = useState(studentAnswers)
  const isPreview = mode === 'preview'
  const hidePreviewDescription = isPreview && hideSolveDescription
  const isPartial = partialCredit != null && partialCredit > 0 && partialCredit < 1

  if (!supportedType) {
    const fallback = (
      <QuestionResultCard
        order={order}
        isCorrect={isPreview ? undefined : isCorrect}
        partialCredit={isPreview ? undefined : partialCredit}
        questionSnapshot={questionSnapshot}
        studentAnswer={isPreview ? undefined : studentAnswer}
        correctAnswer={correctAnswer}
        explanation={hidePreviewDescription ? undefined : explanation}
        variant="meta"
        hideStudentAnswer={isPreview}
      />
    )
    if (!isPreview) return fallback
    return (
      <PreviewHeader>
        {fallback}
        {!hidePreviewDescription && !hasDisplayableCorrectAnswer(correctAnswer) && !explanation?.trim() ? (
          <p className="text-sm text-gray-600">{t('solutionPreview.empty')}</p>
        ) : null}
      </PreviewHeader>
    )
  }

  const correctAnswers = toAnswerArray(correctAnswer)
  const correctFlag: boolean | null = isPreview
    ? null
    : isPartial
      ? null
    : typeof isCorrect === 'boolean'
      ? isCorrect
      : null
  const solveResult: QuizSolveResult = {
    correct: correctFlag,
    corrects: isPreview
      ? []
      : isPartial
        ? []
      : corrects?.length
        ? corrects
        : studentAnswers.map(() => correctFlag),
    correctAnswers,
    description: hidePreviewDescription ? undefined : resolveQuestionDescription(questionSnapshot, explanation),
  }

  if (mode === 'solve') {
    const isSubjective = supportedType === 'explanation'
    const showSubjectiveGrade = isSubjective && onSubjectiveGrade != null && !hideSubmitButton
    const showObjectiveSubmit = !isSubjective && onSolve != null && !hideSubmitButton
    return (
      <QuizStyleProvider variant="lc">
        <div data-testid="coaching-quiz-editor">
          <QuizRenderer
            mode="student"
            quizType={supportedType}
            quizId={quizId}
            quiz={questionSnapshot as any}
            initialAnswers={studentAnswers}
            submitLabel={t('override.checkAnswer')}
            hideSubmitButton={hideSubmitButton || showSubjectiveGrade || showObjectiveSubmit}
            blankPlaceholder="?"
            disableSound
            onAnswerChange={(answers) => {
              setDraftAnswers(answers)
              onAnswerChange?.(answers)
            }}
          />
          {showObjectiveSubmit ? (
            <SubmitButton
              label={t('override.checkAnswer')}
              onClick={() => onSolve(draftAnswers)}
              disabled={isSubmitting}
              isLoading={isSubmitting}
            />
          ) : null}
          {showSubjectiveGrade ? (
            <SubjectiveGradeSelect
              disabled={isSubmitting || !hasSubmittedAnswer(draftAnswers)}
              correctAnswer={correctAnswer}
              questionSnapshot={questionSnapshot}
              allowPartialCredit={allowSubjectivePartialCredit}
              onChange={({ isCorrect, partialCredit: nextPartialCredit }) => onSubjectiveGrade({
                isCorrect,
                partialCredit: nextPartialCredit,
                answers: draftAnswers,
              })}
            />
          ) : null}
        </div>
      </QuizStyleProvider>
    )
  }

  if (isPreview) {
    return (
      <PreviewHeader>
        <QuizStyleProvider variant="lc">
          <div data-testid="coaching-quiz-preview">
            <QuizRenderer
              mode="student"
              quizType={supportedType}
              quizId={quizId}
              quiz={questionSnapshot}
              initialAnswers={[]}
              isViewSolved
              solveResult={solveResult}
              showReallyTrue
              hideSubmitButton
              hideExplanation
              hideSolveDescription={hideSolveDescription}
              blankPlaceholder="?"
              disableSound
            />
          </div>
        </QuizStyleProvider>
        <PreviewDetails
          quizId={quizId}
          questionSnapshot={questionSnapshot}
          correctAnswer={correctAnswer}
          explanation={explanation}
          hideSolveDescription={hideSolveDescription}
        />
      </PreviewHeader>
    )
  }

  return (
    <QuizStyleProvider variant="lc">
      <div data-testid="coaching-quiz-renderer">
        {isPartial ? (
          <p
            data-testid="coaching-partial-credit"
            className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-700"
          >
            {t('quiz.partial')}
          </p>
        ) : null}
        <QuizRenderer
          mode="student"
          quizType={supportedType}
          quizId={quizId}
          quiz={questionSnapshot as any}
          initialAnswers={studentAnswers}
          isViewSolved
          solveResult={solveResult}
          hideSubmitButton
          blankPlaceholder="?"
          hideSolveDescription={hideSolveDescription}
          disableSound
        />
      </div>
    </QuizStyleProvider>
  )
}
